<template lang="pug">
  .rma-container
    .rma-confirm-return
      .page-title
        h3 {{ __('Order') }} {{ formData.orderNumber || '' }}
        span.date {{ returnDate }}
      Message.msg-extra-padding(
        v-if="additionalInfoRequired"
        status="warning"
      )
        span.font-size-xxs(v-translate)
          | It appears your return requires additional information. Please contact {{ rogue.site.support.phone }} or
          a.link(:href="`mailto:${rogue.site.support.email}`") {{ rogue.site.support.email }}
      section.shipping-addresses(v-if="order?.shippingFromAddress && order?.shippingToAddress")
        Row(align-items="stretch")
          Column(width="6")
            h4.underline {{ __('Shipping From') }}
            Box
              span.name {{ order.customer.name }}
              span.address-line {{ order.shippingFromAddress.address }}
              span.address-line(v-if="order.shippingFromAddress.address2") {{ order.shippingFromAddress.address2 }}
              span.address-line
                | {{ order.shippingFromAddress.city || '' }}, &nbsp;
                | {{ order.shippingFromAddress.state || '' }} &nbsp;
                | {{ order.shippingFromAddress.zip || '' }}
              span.address-line {{ order.shippingFromAddress.country }}
              span.phone {{ order.customer.phone }}
              span.email {{ order.customer.email }}
          Column(width="6")
            h4.underline {{ __('Shipping To') }}
            Box
              span.name {{ __('Rogue Fitness') }}
              span.address-line {{ __('Attn: Returns') }}
              span.address-line {{ order.shippingToAddress.address }}
              span.address-line(v-if="order.shippingToAddress.address2") {{ order.shippingToAddress.address2 }}
              span.address-line(v-if="order.shippingToAddress.name") {{ order.shippingToAddress.name }}
              span.address-line
                | {{ order.shippingToAddress.city || '' }}, &nbsp;
                | {{ order.shippingToAddress.state || '' }} &nbsp;
                | {{ order.shippingToAddress.zip || '' }}
      section(v-if="order?.shippingFromAddress && order?.shippingToAddress")
        h3.underline {{ __('Shipping Options') }}
        p {{ __('Customers are responsible for the return shipping cost.') }}
        loading(v-if="estimating" height="180px")
        Row(align-items="stretch" v-else)
          Column(width="6")
            Radio(
              option="ups"
              name="returnType"
              :disabled="printReturnLabelsDisabled"
              :modelValue="shippingCarrier"
              @update:modelValue="shippingCarrier = $event"
            )
              .shipping-label.font-weight-bold {{ __('Purchase & Print a UPS Label') }}
              .shipping-info
                p(v-translate)
                  | Purchase a return label now that is ready to print and the cost will be deducted from your refund
                  | or charged at the time of an exchange. Drop off your return at any UPS location. &nbsp;
                  a(href="https://www.ups.com/dropoff" rel="noopener noreferrer" @click.stop target="_blank") {{ __('Find a UPS location') }}
                  | .
                span
                  | {{ __('Estimated shipping for your return is') }} &nbsp;
                  span.font-weight-bold {{ estimatedReturn }}

          Column(width="6")
            Radio(
              :option="null"
              :disabled="additionalInfoRequired"
              name="returnType"
              :modelValue="shippingCarrier"
              @update:modelValue="shippingCarrier = $event"
            )
              .shipping-label.font-weight-bold {{ __('Ship Your Way') }}
              .shipping-info
                p(v-translate)
                  | Ship and pay for your return using your preferred shipping method.
                  | No shipping charge will be added to your previous order.
      section
        .title.font-weight-bold {{ __('Products to Return') }}
        .product(
          v-for="product in formData.items"
          :key="product.sku"
        )
          rma-product(
            :product="order.items.find(i => i.sku === product.sku)"
            :return-product="product"
            detailed-info
          )
      section
        Row
          Column(width="4")
            RogueButton.form-field(
              :text="__('Back')"
              @click="router.push({ path: '/rmaprocess/select-items', query: route.query })"
            )
          Column(width="4")
          Column(width="4")
            RogueButton.form-field(
              :text="__('Submit')"
              :disabled="additionalInfoRequired || Boolean(error)"
              :loading="processing"
              color="black"
              @click="submit"
            )
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import RmaProduct from '../components/rma-product.vue'
import { DenyReasons, ReturnReasons, formatDate } from '../helpers'
import type { FormData, RMAOrder, RMASuccess, RMAError } from '../types'
import { useRogue } from '@/js/services/Rogue'

type ShippingRate = {
  carrier: string
  code: string
  rate: number | string
}

type Props = {
  formData: FormData
  order: RMAOrder
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'success', response: RMASuccess): void
}>()

const router = useRouter()
const route = useRoute()
const rogue = useRogue()

const processing = ref<boolean>(false)
const estimating = ref<boolean>(false)
const error = ref<string | null>(null)
const shippingCarrier = ref<string | null>(null)
const rates = ref<ShippingRate[]>([])

const returnDate = computed(() => {
  return formatDate(new Date(Date.now()).toISOString())
})

const shippingRate = computed<ShippingRate | null>(() => {
  if (!rates.value.length) {
    return null
  }

  return rates.value.find(rate => rate.carrier === shippingCarrier.value) || null
})

const estimatedReturn = computed(() => {
  if (rates.value.length) {
    const ups = rates.value.find(r => r.carrier === 'ups')

    return ups ? rogue.currency(ups.rate.toString() || '') : rogue.currency('')
  }

  return rogue.currency('')
})

const printReturnLabelsDisabled = computed(() => {
  return !rates.value.length || additionalInfoRequired.value || rogue.site.code === 'CAN'
})

const additionalInfoRequired = computed(() => {
  const prevent = props.formData?.items?.some(item => (Object.keys(DenyReasons).includes(item.returnReason))) || false

  return (prevent && !estimating.value)
    || Boolean(error.value)
    || (shippingCarrier.value && !rates.value.length)
})

watch(shippingCarrier, (value) => {
  if (props.formData) {
    props.formData.shippingCode = rates.value.find(rate => rate.carrier === value)?.code || null
  }
  error.value = null
})

onMounted(async () => {
  if (!additionalInfoRequired.value && props.order?.token && props.formData?.items?.length) {
    try {
      estimating.value = true
      rates.value = await rogue.fetch<ShippingRate[]>(
        '/svc_rma/order/shipping-rates',
        'POST',
        { token: props.order.token, items: props.formData.items }
      )

      shippingCarrier.value = rates.value.length && rogue.site.code !== 'CAN' ? 'ups' : null
    } catch (e) {
      rogue.log.error('RMA: Unable to get shipping rates', e)
    } finally {
      estimating.value = false
    }
  }

  if (props.formData) {
    props.formData.shippingCode = shippingRate.value?.code || null
  }
})

async function submit() {
  try {
    processing.value = true

    props.formData.items.forEach(item => {
      item.returnReason = (ReturnReasons as Record<string, string>)[item.returnReason] || item.returnReason
    })

    const response = await rogue.fetch<RMASuccess | RMAError>(
      '/svc_rma/rma',
      'POST',
      { ...props.formData, shippingRate: shippingRate.value }
    )

    if (!response.success) {
      error.value = ('errorMessage' in response) ? response.errorMessage : null
    } else if (response.success) {
      emit('success', response as RMASuccess)
      router.push({ path: '/rmaprocess/print', query: route.query })
    }
  } catch (e) {
    rogue.log.error('RMA: An error occurred while submitting the order return', e)
    error.value = (e as Error).message
  } finally {
    processing.value = false
  }
}
</script>

<style lang="scss" scoped>
.column {
  display: flex;
  flex-direction: column;
  flex-flow: column;

  .box, .radio-container {
    flex: 1 1 auto;
  }
}

.page-title {
  h3 {
    display: inline-block;
    width: auto;
    margin-bottom: 0;
  }

  .date {
    font-size: $size-s;
    color: $color-gray;
    margin-left: $space-s;
  }
}

.message {
  :deep(.content) {
    display: block;
    text-align: center;
  }

  a {
    margin-left: $space-xxxs;
  }
}

section, .page-title, .message {
  margin-bottom: $space-m;
}

.name, .address-line, .phone, .email, .shipping-label {
  font-size: $size-xs;
  line-height: $line-height-s;
}

.name {
  display: block;
  margin-bottom: $space-s;
  font-weight: $weight-bold;
}

.address-line, .phone, .email {
  display: block;
  margin-bottom: $space-xxs;

  &:last-child {
    margin-bottom: 0;
  }
}

.rma-container .radio-container:deep() {
  .radio {
    align-items: flex-start;
    height: 100%;
    padding: $space-s;
  }

  input {
    width: 10px;
    flex-shrink: 0;
  }

  flex-wrap: nowrap;
}

.shipping-label {
  margin-bottom: $space-xs;
}

.shipping-info {
  p, span {
    line-height: $line-height-l;
  }
}

@include mobile {
  .column:not(:first-child) .radio-container {
    border-top: none;
  }
  .shipping-info {
    p, span {
      font-size: $size-xs;
    }
  }
}

@include not-mobile {
  .name, .address-line, .phone, .email, .shipping-label {
    font-size: $size-s;
  }
  .shipping-info {
    p, span {
      font-size: $size-s;
    }
  }
}
</style>
